import { useEffect, useMemo, useState } from "react";
import api, { formatError, formatRupiah } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, PackagePlus, PackageMinus, Package, Loader2 } from "lucide-react";

const emptyForm = { name: "", sku: "", category: "", stock: "", buy_price: "", sell_price: "" };

export default function Stock() {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [movementItem, setMovementItem] = useState(null);
  const [movementType, setMovementType] = useState("in");
  const [movement, setMovement] = useState({ quantity: "", note: "" });
  const [moving, setMoving] = useState(false);

  const fetchItems = () => api.get("/items").then((res) => setItems(res.data)).catch(() => setItems([]));
  useEffect(() => { fetchItems(); }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = search.toLowerCase();
    return items.filter((i) => i.name.toLowerCase().includes(q) || i.sku.toLowerCase().includes(q) || i.category.toLowerCase().includes(q));
  }, [items, search]);

  const openAdd = () => {
    setEditingItem(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name,
      sku: item.sku,
      category: item.category,
      stock: String(item.stock),
      buy_price: String(item.buy_price),
      sell_price: String(item.sell_price),
    });
    setFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name,
      sku: form.sku,
      category: form.category,
      stock: parseInt(form.stock) || 0,
      buy_price: parseFloat(form.buy_price) || 0,
      sell_price: parseFloat(form.sell_price) || 0,
    };
    try {
      if (editingItem) {
        await api.put(`/items/${editingItem.id}`, payload);
        toast.success(`Barang "${payload.name}" berhasil diubah`);
      } else {
        await api.post("/items", payload);
        toast.success(`Barang "${payload.name}" berhasil ditambahkan`);
      }
      setFormOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(formatError(err, "Gagal menyimpan barang"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/items/${deletingItem.id}`);
      toast.success(`Barang "${deletingItem.name}" dihapus`);
      setDeletingItem(null);
      fetchItems();
    } catch (err) {
      toast.error(formatError(err, "Gagal menghapus barang"));
    }
  };

  const openMovement = (item, type) => {
    setMovementItem(item);
    setMovementType(type);
    setMovement({ quantity: "", note: "" });
  };

  const handleMovement = async (e) => {
    e.preventDefault();
    setMoving(true);
    try {
      const res = await api.post(`/items/${movementItem.id}/movement`, {
        type: movementType,
        quantity: parseInt(movement.quantity) || 0,
        note: movement.note,
      });
      toast.success(`Stok "${movementItem.name}" sekarang ${res.data.stock}`);
      setMovementItem(null);
      fetchItems();
    } catch (err) {
      toast.error(formatError(err, "Gagal mengubah stok"));
    } finally {
      setMoving(false);
    }
  };

  if (items === null) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="stock-loading">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stock-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Stok Barang</h1>
          <p className="text-sm text-slate-500 mt-1">Kelola daftar barang, tambah, edit, dan catat stok masuk/keluar</p>
        </div>
        <Button onClick={openAdd} data-testid="add-item-button" className="bg-indigo-600 hover:bg-indigo-700 text-white transition-colors duration-200">
          <Plus className="w-4 h-4 mr-2" /> Tambah Barang
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="stock-search-input"
          placeholder="Cari nama, SKU, atau kategori..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 bg-white focus-visible:ring-indigo-500"
        />
      </div>

      <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid="items-table-card">
        <CardContent className="p-0">
          <div className="overflow-auto">
            <Table data-testid="items-table">
              <TableHeader>
                <TableRow className="bg-slate-50/50 border-b border-slate-200">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Nama Barang</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">SKU</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Kategori</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Stok</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Harga Beli</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Harga Jual</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-16" data-testid="items-empty">
                      <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-400">{search ? "Tidak ada barang yang cocok" : "Belum ada barang. Klik Tambah Barang untuk mulai."}</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((item) => (
                    <TableRow key={item.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-200" data-testid={`item-row-${item.id}`}>
                      <TableCell className="p-4 text-sm font-medium text-slate-800">{item.name}</TableCell>
                      <TableCell className="p-4 text-sm text-slate-500">{item.sku || "-"}</TableCell>
                      <TableCell className="p-4">
                        {item.category ? (
                          <Badge variant="secondary" className="bg-indigo-50 text-indigo-600 hover:bg-indigo-50">{item.category}</Badge>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="p-4 text-sm text-right">
                        <span className={`font-semibold ${item.stock <= 5 ? "text-rose-600" : "text-slate-800"}`} data-testid={`item-stock-${item.id}`}>
                          {item.stock}
                        </span>
                      </TableCell>
                      <TableCell className="p-4 text-sm text-slate-600 text-right">{formatRupiah(item.buy_price)}</TableCell>
                      <TableCell className="p-4 text-sm text-slate-600 text-right">{formatRupiah(item.sell_price)}</TableCell>
                      <TableCell className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost" size="icon"
                            data-testid={`edit-item-button-${item.id}`}
                            onClick={() => openEdit(item)}
                            title="Edit barang"
                            className="text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            data-testid={`stock-in-button-${item.id}`}
                            onClick={() => openMovement(item, "in")}
                            title="Stok masuk"
                            className="text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                          >
                            <PackagePlus className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            data-testid={`stock-out-button-${item.id}`}
                            onClick={() => openMovement(item, "out")}
                            title="Stok keluar"
                            className="text-slate-500 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          >
                            <PackageMinus className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            data-testid={`delete-item-button-${item.id}`}
                            onClick={() => setDeletingItem(item)}
                            title="Hapus barang"
                            className="text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-md" data-testid="item-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading" data-testid="item-form-title">
              {editingItem ? `Edit Barang: ${editingItem.name}` : "Tambah Barang Baru"}
            </DialogTitle>
            <DialogDescription>
              {editingItem ? "Ubah nama, jumlah stok, harga, atau detail barang lainnya." : "Isi detail barang yang ingin ditambahkan."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="item-form">
            <div className="space-y-2">
              <Label htmlFor="name">Nama Barang</Label>
              <Input id="name" data-testid="item-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="focus-visible:ring-indigo-500" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sku">SKU / Kode</Label>
                <Input id="sku" data-testid="item-sku-input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="Opsional" className="focus-visible:ring-indigo-500" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Kategori</Label>
                <Input id="category" data-testid="item-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Opsional" className="focus-visible:ring-indigo-500" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="stock">Jumlah Stok</Label>
              <Input id="stock" type="number" min="0" data-testid="item-stock-input" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} required className="focus-visible:ring-indigo-500" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="buy_price">Harga Beli (Rp)</Label>
                <Input id="buy_price" type="number" min="0" data-testid="item-buy-price-input" value={form.buy_price} onChange={(e) => setForm({ ...form, buy_price: e.target.value })} required className="focus-visible:ring-indigo-500" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sell_price">Harga Jual (Rp)</Label>
                <Input id="sell_price" type="number" min="0" data-testid="item-sell-price-input" value={form.sell_price} onChange={(e) => setForm({ ...form, sell_price: e.target.value })} required className="focus-visible:ring-indigo-500" />
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" data-testid="item-form-cancel" onClick={() => setFormOpen(false)}>Batal</Button>
              <Button type="submit" data-testid="item-form-submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white transition-colors">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingItem ? "Simpan Perubahan" : "Tambah Barang"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!movementItem} onOpenChange={(open) => !open && setMovementItem(null)}>
        <DialogContent className="sm:max-w-md" data-testid="movement-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading" data-testid="movement-dialog-title">
              {movementType === "in" ? "Stok Masuk" : "Stok Keluar"}: {movementItem?.name}
            </DialogTitle>
            <DialogDescription>
              Stok saat ini: <span className="font-semibold text-slate-700">{movementItem?.stock}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleMovement} className="space-y-4" data-testid="movement-form">
            <div className="space-y-2">
              <Label htmlFor="qty">Jumlah</Label>
              <Input id="qty" type="number" min="1" data-testid="movement-quantity-input" value={movement.quantity} onChange={(e) => setMovement({ ...movement, quantity: e.target.value })} required className="focus-visible:ring-indigo-500" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="note">Catatan</Label>
              <Input id="note" data-testid="movement-note-input" value={movement.note} onChange={(e) => setMovement({ ...movement, note: e.target.value })} placeholder="Opsional" className="focus-visible:ring-indigo-500" />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" data-testid="movement-cancel" onClick={() => setMovementItem(null)}>Batal</Button>
              <Button
                type="submit" data-testid="movement-submit" disabled={moving}
                className={movementType === "in" ? "bg-emerald-600 hover:bg-emerald-700 text-white transition-colors" : "bg-amber-600 hover:bg-amber-700 text-white transition-colors"}
              >
                {moving ? <Loader2 className="w-4 h-4 animate-spin" /> : movementType === "in" ? "Tambah Stok" : "Kurangi Stok"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletingItem} onOpenChange={(open) => !open && setDeletingItem(null)}>
        <AlertDialogContent data-testid="delete-item-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Hapus Barang?</AlertDialogTitle>
            <AlertDialogDescription>
              Barang <span className="font-semibold">{deletingItem?.name}</span> akan dihapus permanen beserta riwayat stoknya. Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-item-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-item-confirm" onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700 text-white transition-colors">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
